import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Loader2, ArrowLeft, AlertTriangle, Receipt, Wrench, ClipboardCheck,
  Clock, ExternalLink, RefreshCcw, TrendingDown, Mail,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { useAuth } from "@/contexts/AuthContext";

const eur = (v) =>
  (Number(v) || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

const STAGE_LABELS = {
  lead: "Lead",
  tech_check: "Tech-Check",
  offer_created: "Angebot erstellt",
  offer_sent: "Angebot versendet",
  offer_signed: "Angebot unterschrieben",
  down_payment_received: "Anzahlung erhalten",
  installer_handover: "Installation-Übergabe",
  heat_load_appointment: "Heizlast + Verbaucheck",
  installation_scheduled: "Installation geplant",
  installation_start: "Installation gestartet",
  installation_done: "Installation abgeschlossen",
  commissioning: "Inbetriebnahme",
};

const Section = ({ title, icon: Icon, accentColor, count, total, children, testid, severity = "info" }) => {
  const bgMap = {
    danger: "bg-rose-500/10 border-rose-500/30 text-rose-300",
    warning: "bg-amber-500/10 border-amber-500/30 text-amber-300",
    info: "bg-sky-500/10 border-sky-500/30 text-sky-300",
    ok: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
  };
  const tone = count === 0 ? bgMap.ok : bgMap[severity];

  return (
    <section data-testid={testid} className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4 sm:p-5 min-w-0 overflow-hidden">
      <header className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-xl border flex items-center justify-center ${tone}`}>
            <Icon size={20} />
          </div>
          <div>
            <h2 className="font-display text-[17px] font-bold leading-tight">{title}</h2>
            <p className="text-[12px] text-zinc-400 mt-0.5 font-medium">
              {count === 0 ? "Alles im grünen Bereich" : `${count} Eintrag${count === 1 ? "" : "e"}`}
              {total != null && count > 0 ? ` · ${eur(total)} offen` : ""}
            </p>
          </div>
        </div>
        <span className={`text-[24px] font-display font-bold tabular-nums ${count === 0 ? "text-emerald-400" : "text-zinc-100"}`}>
          {count}
        </span>
      </header>
      {children}
    </section>
  );
};

const Row = ({ left, right, to }) => {
  const Wrapper = to ? Link : "div";
  const linkProps = to ? { to } : {};
  return (
    <Wrapper
      {...linkProps}
      className={`flex items-center justify-between gap-3 py-2.5 px-3 -mx-3 rounded-lg ${
        to ? "hover:bg-zinc-800/50 cursor-pointer transition group" : ""
      }`}
    >
      <div className="min-w-0 flex-1">{left}</div>
      <div className="flex items-center gap-2 shrink-0 text-zinc-400 group-hover:text-[#00E575] transition">
        {right}
        {to && <ExternalLink size={14} />}
      </div>
    </Wrapper>
  );
};

export default function AdminHealth() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    if (silent) setRefreshing(true);
    try {
      const r = await api.get("/admin/health-dashboard");
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 403) {
        navigate("/dashboard");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const sendDigestNow = async () => {
    if (!window.confirm("Den wöchentlichen Pipeline-Pulse jetzt sofort an alle Admins + Vertriebsdirektoren versenden?")) return;
    setSendingDigest(true);
    try {
      const r = await api.post("/admin/health-digest/trigger?force=true");
      if (r.data?.sent > 0) {
        toast.success(`Digest an ${r.data.sent} Empfänger versendet`);
      } else if (r.data?.failed > 0) {
        toast.error(`Versand an ${r.data.failed} Empfänger fehlgeschlagen – siehe E-Mail-Logs`);
      } else {
        toast.info("Keine Empfänger gefunden");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Versand fehlgeschlagen");
    } finally {
      setSendingDigest(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B]">
        <AppHeader back="/dashboard" />
        <div className="flex items-center justify-center py-32 text-zinc-400">
          <Loader2 className="animate-spin mr-2" /> Lade Pipeline-Health…
        </div>
      </div>
    );
  }

  if (!data) return null;

  const totalIssues =
    (data.unpaid_invoices?.count || 0) +
    (data.overdue_handovers?.count || 0) +
    (data.missing_tech_check?.count || 0) +
    (data.stale_projects?.count || 0);

  return (
    <div className="min-h-screen bg-[#09090B] pb-16">
      <AppHeader back="/dashboard" />

      <div className="max-w-5xl mx-auto px-4 pt-4 pb-6">
        <div className="flex items-center justify-between gap-3 mb-5">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase font-bold tracking-wider text-[#00E575] mb-1">
              <AlertTriangle size={14} />
              Pipeline Pulse
            </div>
            <h1 className="font-display text-[28px] sm:text-[32px] font-bold leading-tight">
              Health-Dashboard
            </h1>
            <p className="text-sm text-zinc-400 mt-1">
              {totalIssues === 0 ? (
                <span className="text-emerald-400 font-medium">Alle Pipelines laufen sauber – nichts zu tun. 🎉</span>
              ) : (
                <>Insgesamt <b className="text-zinc-200">{totalIssues}</b> Vorgang{totalIssues === 1 ? "" : "e"}, der/die Aufmerksamkeit brauch{totalIssues === 1 ? "t" : "en"}.</>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {user?.role === "admin" && (
              <button
                data-testid="admin-health-send-digest-btn"
                onClick={sendDigestNow}
                disabled={sendingDigest}
                className="h-10 px-3 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm flex items-center gap-2 transition active:scale-95 disabled:opacity-50"
                title="Jetzt Pipeline-Pulse-Email an alle Admins + Direktoren senden"
              >
                <Mail size={14} className={sendingDigest ? "animate-pulse" : ""} />
                <span className="hidden sm:inline">{sendingDigest ? "Sende…" : "Digest senden"}</span>
              </button>
            )}
            <button
              data-testid="admin-health-refresh-btn"
              onClick={() => load(true)}
              disabled={refreshing}
              className="h-10 px-3 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm flex items-center gap-2 transition active:scale-95"
            >
              <RefreshCcw size={14} className={refreshing ? "animate-spin" : ""} />
              <span className="hidden sm:inline">Aktualisieren</span>
            </button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {/* Unpaid invoices */}
          <Section
            testid="health-card-unpaid"
            title="Offene Anzahlungsrechnungen"
            icon={Receipt}
            severity="danger"
            count={data.unpaid_invoices?.count || 0}
            total={data.unpaid_invoices?.total_outstanding_gross}
          >
            {(data.unpaid_invoices?.items || []).slice(0, 8).map((inv) => (
              <Row
                key={inv.invoice_id}
                to={`/projects/${inv.project_id}?tab=offer`}
                left={
                  <div>
                    <div className="text-sm font-medium text-zinc-100 truncate">
                      {inv.customer_name || "—"} · {inv.kind} ({inv.percent}%)
                    </div>
                    <div className="text-[12px] text-zinc-300 truncate">
                      {inv.project_number} · {inv.customer_city || ""}
                    </div>
                  </div>
                }
                right={<span className="text-sm font-semibold text-rose-300">{eur(inv.amount_gross)}</span>}
              />
            ))}
            {(data.unpaid_invoices?.count || 0) > 8 && (
              <div className="text-[12px] text-zinc-300 text-center mt-2">
                +{data.unpaid_invoices.count - 8} weitere
              </div>
            )}
          </Section>

          {/* Overdue handovers */}
          <Section
            testid="health-card-handovers"
            title="Überfällige Installation-Übergaben"
            icon={Wrench}
            severity="warning"
            count={data.overdue_handovers?.count || 0}
          >
            {(data.overdue_handovers?.items || []).slice(0, 8).map((p) => (
              <Row
                key={p.project_id}
                to={`/projects/${p.project_id}?tab=status`}
                left={
                  <div>
                    <div className="text-sm font-medium text-zinc-100 truncate">{p.customer_name || "—"}</div>
                    <div className="text-[12px] text-zinc-300 truncate">
                      {p.project_number} · {p.customer_city || ""}
                    </div>
                  </div>
                }
                right={
                  <span className="text-sm font-semibold text-amber-300">
                    {p.days_since_payment}d
                  </span>
                }
              />
            ))}
            {(data.overdue_handovers?.count || 0) > 8 && (
              <div className="text-[12px] text-zinc-300 text-center mt-2">
                +{data.overdue_handovers.count - 8} weitere
              </div>
            )}
          </Section>

          {/* Missing tech check */}
          <Section
            testid="health-card-missing-tech"
            title="Verkauft ohne Tech-Check"
            icon={ClipboardCheck}
            severity="warning"
            count={data.missing_tech_check?.count || 0}
          >
            {(data.missing_tech_check?.items || []).slice(0, 8).map((p) => (
              <Row
                key={p.project_id}
                to={`/projects/${p.project_id}?tab=tech`}
                left={
                  <div>
                    <div className="text-sm font-medium text-zinc-100 truncate">{p.customer_name || "—"}</div>
                    <div className="text-[12px] text-zinc-300 truncate">
                      {p.project_number} · {STAGE_LABELS[p.stage] || p.stage}
                    </div>
                  </div>
                }
                right={<TrendingDown size={16} />}
              />
            ))}
            {(data.missing_tech_check?.count || 0) > 8 && (
              <div className="text-[12px] text-zinc-300 text-center mt-2">
                +{data.missing_tech_check.count - 8} weitere
              </div>
            )}
          </Section>

          {/* Stale projects */}
          <Section
            testid="health-card-stale"
            title={`Stille Projekte (>${data.thresholds.stale_project_days} Tage)`}
            icon={Clock}
            severity="info"
            count={data.stale_projects?.count || 0}
          >
            {(data.stale_projects?.items || []).slice(0, 8).map((p) => (
              <Row
                key={p.project_id}
                to={`/projects/${p.project_id}`}
                left={
                  <div>
                    <div className="text-sm font-medium text-zinc-100 truncate">{p.customer_name || "—"}</div>
                    <div className="text-[12px] text-zinc-300 truncate">
                      {p.project_number} · {STAGE_LABELS[p.stage] || p.stage}
                    </div>
                  </div>
                }
                right={
                  <span className="text-sm font-semibold text-sky-300">{p.days_stale}d</span>
                }
              />
            ))}
            {(data.stale_projects?.count || 0) > 8 && (
              <div className="text-[12px] text-zinc-300 text-center mt-2">
                +{data.stale_projects.count - 8} weitere
              </div>
            )}
          </Section>
        </div>

        <div className="mt-6 text-[12px] text-zinc-400 text-center">
          Stand: {new Date(data.generated_at).toLocaleString("de-DE")}
          {" · Schwellwerte: "}
          {data.thresholds.handover_overdue_days}d für Übergabe,
          {" "}{data.thresholds.stale_project_days}d für stille Projekte
          {" · Automatischer E-Mail-Versand: jeden Montag 07:00 Uhr an Admins + Vertriebsdirektoren"}
        </div>
      </div>
    </div>
  );
}
